import { auth } from '../firebase'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'

async function buildHeaders({ hasBody, requiresAuth } = { hasBody: false, requiresAuth: true }) {
  const headers = {}

  if (hasBody) {
    headers['Content-Type'] = 'application/json'
  }

  if (requiresAuth) {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('You must be signed in to perform this action.')
    }
    const token = await currentUser.getIdToken()
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

async function request(endpoint, { method = 'GET', data, requiresAuth = true } = {}) {
  const hasBody = data !== undefined
  const headers = await buildHeaders({ hasBody, requiresAuth })

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: hasBody ? JSON.stringify(data) : undefined,
  })

  let payload = null
  if (response.status !== 204) {
    const text = await response.text()
    if (text) {
      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json') || (text.trim().startsWith('{') || text.trim().startsWith('['))) {
        try {
          payload = JSON.parse(text)
        } catch (e) {
          // If JSON parsing fails, treat as error
          throw new Error(`Invalid response: ${text.substring(0, 100)}`)
        }
      } else {
        // HTML error page - extract error message if possible
        throw new Error(`Server error: ${response.status} ${response.statusText}`)
      }
    }
  }

  if (!response.ok) {
    const message = payload?.error || response.statusText || 'Request failed'
    throw new Error(message)
  }

  return payload
}

export const api = {
  get: (endpoint, options) => request(endpoint, { method: 'GET', ...(options || {}) }),
  post: (endpoint, data, options) => request(endpoint, { method: 'POST', data, ...(options || {}) }),
  put: (endpoint, data, options) => request(endpoint, { method: 'PUT', data, ...(options || {}) }),
  patch: (endpoint, data, options) => request(endpoint, { method: 'PATCH', data, ...(options || {}) }),
  delete: (endpoint, options) => request(endpoint, { method: 'DELETE', ...(options || {}) }),
}

// Box API
export const boxApi = {
  getAll: () => api.get('/api/boxes'),
  getById: (id) => api.get(`/api/boxes/${id}`),
  create: (data) => api.post('/api/boxes', data),
  clone: (id, data) => api.post(`/api/boxes/${id}/clone`, data),
  update: (id, data) => api.put(`/api/boxes/${id}`, data),
  updateSchedule: (id, data) => api.put(`/api/boxes/${id}/schedule`, data),
  delete: (id, data) => api.delete(`/api/boxes/${id}`, { data }),
  getMembers: (id) => api.get(`/api/boxes/${id}/members`),
  addSelfToBox: (id) => api.post(`/api/boxes/${id}/members/self`),
  updateMember: (boxId, memberId, data) => api.put(`/api/boxes/${boxId}/members/${memberId}`, data),
  promoteMember: (boxId, memberId) => api.post(`/api/boxes/${boxId}/members/${memberId}/promote`),
  removeMember: (boxId, memberId, data) =>
    api.post(`/api/boxes/${boxId}/members/${memberId}/remove`, data),
  getSchedule: (id) => api.get(`/api/boxes/${id}/schedule`),
  updatePayoutDate: (id, data) => api.patch(`/api/boxes/${id}/schedule/payout-date`, data),
  getLedger: (id) => api.get(`/api/boxes/${id}/ledger`),
  createLedgerEntry: (id, data) => api.post(`/api/boxes/${id}/ledger`, data),
  voidLedgerEntry: (boxId, entryId, data) =>
    api.post(`/api/boxes/${boxId}/ledger/${entryId}/void`, data),
  createInvitation: (id, data) => api.post(`/api/boxes/${id}/invitations`, data),
  getInvitations: (id) => api.get(`/api/boxes/${id}/invitations`),
  getAuditLogs: (id) => api.get(`/api/boxes/${id}/audit`),
  analyze: (id) => api.post(`/api/boxes/${id}/analyze`),
  getInsights: (id) => api.get(`/api/boxes/${id}/analyze`),
  getMessages: (id) => api.get(`/api/boxes/${id}/messages`),
  getMessage: (boxId, messageId) => api.get(`/api/boxes/${boxId}/messages/${messageId}`),
  createMessage: (id, data) => api.post(`/api/boxes/${id}/messages`, data),
  updateMessage: (boxId, messageId, data) => api.put(`/api/boxes/${boxId}/messages/${messageId}`, data),
  deleteMessage: (boxId, messageId, options) => api.delete(
    `/api/boxes/${boxId}/messages/${messageId}`,
    options
  ),
  createReply: (boxId, messageId, data) => api.post(`/api/boxes/${boxId}/messages/${messageId}/replies`, data),
  updateReply: (boxId, messageId, replyId, data) => api.put(`/api/boxes/${boxId}/messages/${messageId}/replies/${replyId}`, data),
  deleteReply: (boxId, messageId, replyId) => api.delete(`/api/boxes/${boxId}/messages/${messageId}/replies/${replyId}`),
};

// Invitation API
export const invitationApi = {
  getById: (id) => api.get(`/api/invitations/${id}`),
  accept: (id, data) => api.put(`/api/invitations/${id}`, { ...data, status: 'accepted' }),
  decline: (id) => api.put(`/api/invitations/${id}`, { status: 'declined' }),
  resend: (id) => api.post(`/api/invitations/${id}/resend`),
  remove: (id) => api.delete(`/api/invitations/${id}`),
};

// Ledger API
export const ledgerApi = {
  getById: (id) => api.get(`/api/ledger/${id}`),
  update: (id, data) => api.put(`/api/ledger/${id}`, data),
  delete: (id) => api.delete(`/api/ledger/${id}`),
};

// User Activity API
export const userApi = {
  getContributions: (boxId) => {
    const query = boxId ? `?box_id=${encodeURIComponent(boxId)}` : '';
    return api.get(`/api/me/contributions${query}`);
  },
};

// Feedback API
export const feedbackApi = {
  submit: (data) => api.post('/api/feedback', data),
};
